/**
 * startActivityLog.js
 *
 * Per-lead behavioural log for the Start portal (/start/*).
 *
 * GA4 (analyticsService.js) answers "how is the Start portal doing overall".
 * This answers "what did THIS lead do, and when", so the CRM Lead overview →
 * Notes timeline can show it in blue next to logins, the NDA and the assessment.
 *
 * Append-only: one doc per occurrence, so a lead who opens the dashboard four
 * times gets four timestamped rows and you can see the shape of the session.
 *
 *   users/{uid}/startActivity/{autoId}
 *   { kind, key, label, detail, path, at }
 *
 * Writes are best-effort: tracking must never break the page the lead is on.
 */

import { addDoc, collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';

export const START_ACTIVITY_COLLECTION = 'startActivity';

// Most recent entries pulled into the CRM timeline for one lead.
const START_ACTIVITY_READ_LIMIT = 500;

// ---------------------------------------------------------------------------
// Page labels
// ---------------------------------------------------------------------------

/**
 * Friendly labels for the /start routes worth logging, keyed by the path with
 * the /start prefix stripped ('' = the dashboard index).
 *
 * A route that is NOT in here is deliberately not logged — return/callback
 * pages the lead never chose to visit (checkout/return, nda-complete) and the
 * internal-only assessment-analysis view would be noise in the timeline.
 */
const START_PAGE_LABELS = {
  '': 'Start dashboard',
  'dashboard': 'Start dashboard',
  'assessment': 'Business assessment',
  'courses': 'Courses',
  'community': 'Community',
  'roadmap': 'Roadmap',
  'profile': 'Profile',

  // The pages Start leads shop from
  'program': 'Start Program page',
  'programdetail': 'Program details',
  'customprogram': 'Custom program builder',
  'program-call': 'Program call',
  'alacarte': 'À la carte',
  'hourly': 'Hourly services',
  'solutions': 'Solutions',
  'proposal': 'Proposal builder',
  'checkout': 'Checkout',
  'contract': 'Contract',
  'nda': 'NDA',

  // Marketing / trust pages
  'team': 'Meet the Team',
  'success-stories': 'Success Stories',
  'cqc-home': 'CQC Home',
  'process': 'Our Process',
  'breakthrough-session': 'Breakthrough Session',

  // Discovery flow
  'discovery': 'Discovery',
  'existing-conditions': 'Existing Conditions',
  'existing-direction': 'Existing Direction',
  'existing-org-chart': 'Org Chart',

  // Department pages and their tools
  'sales': 'Sales',
  'sales/close-ratio': 'Sales — Close Ratio',
  'sales/proposals': 'Sales — Proposals',
  'sales-referral-programs': 'Sales — Referral Programs',
  'salesrevenue': 'Sales — Revenue',
  'operations': 'Operations',
  'operations/sops': 'Operations — SOPs',
  'operations/kpis': 'Operations — KPIs',
  'operations/job-descriptions': 'Operations — Job Descriptions',
  'operations/gpmoperations': 'Operations — GPM',
  'finance': 'Finance',
  'finance/startlaborburden': 'Finance — Labor Burden',
  'marketing': 'Marketing',
  'technology': 'Technology',
  'technology/estimating-template': 'Technology — Estimating Template',
  'technology/chatgpt': 'Technology — Custom GPTs',
  'technology/crmtechstack': 'Technology — Tech Stack',
  'hr': 'HR',
};

/** Strip the /start prefix and any trailing slash so a path can hit the label map. */
function normalizeStartPath(pathname) {
  if (typeof pathname !== 'string') return null;
  if (!pathname.startsWith('/start')) return null;
  return pathname
    .slice('/start'.length)
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .toLowerCase();
}

/**
 * Human label for a /start path, or null when the page is not worth logging.
 * @param {string} pathname
 * @returns {string|null}
 */
export function resolveStartPageLabel(pathname) {
  const key = normalizeStartPath(pathname);
  if (key == null) return null;
  return START_PAGE_LABELS[key] ?? null;
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** Firestore rejects undefined; drop those keys rather than failing the write. */
function pruneUndefined(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined) out[k] = v;
  });
  return Object.keys(out).length ? out : null;
}

// A remount or a StrictMode double-effect would otherwise write the same visit
// twice within milliseconds. Kept short so genuine back-and-forth navigation
// still lands as separate rows.
const DUPLICATE_WINDOW_MS = 3000;
const lastWriteByKey = new Map();

function isDuplicate(uid, key) {
  const cacheKey = `${uid}:${key}`;
  const now = Date.now();
  const previous = lastWriteByKey.get(cacheKey);
  if (previous && now - previous < DUPLICATE_WINDOW_MS) return true;
  lastWriteByKey.set(cacheKey, now);
  return false;
}

/**
 * Record one thing the lead did on the Start side. Each call is its own row.
 *
 * @param {object} user           - Firebase auth user (needs .uid)
 * @param {object} options
 * @param {string} options.key    - stable identifier, e.g. 'page_team' or 'program_chosen'
 * @param {string} options.label  - human label shown in the CRM timeline
 * @param {string} [options.kind] - 'page_view' | 'program_viewed' | 'program_customized' | 'program_chosen'
 * @param {object} [options.detail] - extra context (program name, price, deliverables…)
 * @param {string} [options.path]   - the /start path this happened on
 */
export async function logStartActivity(user, { key, label, kind = 'action', detail = null, path = null } = {}) {
  const uid = user?.uid;
  if (!uid || !key || !label) return;
  if (isDuplicate(uid, key)) return;

  try {
    const entry = {
      kind,
      key,
      label,
      at: new Date().toISOString(),
    };
    if (path) entry.path = path;

    const cleanDetail = pruneUndefined(detail);
    if (cleanDetail) entry.detail = cleanDetail;

    await addDoc(collection(db, 'users', uid, START_ACTIVITY_COLLECTION), entry);
  } catch (error) {
    console.warn('[startActivity] failed to log', key, error);
  }
}

/**
 * Log a Start page visit. No-ops for paths that aren't in the label map.
 * @param {object} user
 * @param {string} pathname
 */
export function logStartPageView(user, pathname) {
  const label = resolveStartPageLabel(pathname);
  if (!label) return;
  void logStartActivity(user, {
    key: `page_${normalizeStartPath(pathname) || 'dashboard'}`,
    label: `Visited ${label}`,
    kind: 'page_view',
    path: pathname,
  });
}

// ---------------------------------------------------------------------------
// Typed helpers for the program flow
// ---------------------------------------------------------------------------

/**
 * The lead opened a specific program's detail page.
 * @param {object} user
 * @param {string} programName
 * @param {string} [tier]
 */
export function logProgramViewed(user, programName, tier) {
  if (!programName) return;
  void logStartActivity(user, {
    key: `program_viewed_${programName}`,
    label: `Looked at the ${programName} program`,
    kind: 'program_viewed',
    detail: pruneUndefined({ programName, tier: tier || undefined }),
    path: '/start/programdetail',
  });
}

/**
 * The lead changed a program's deliverables away from its defaults.
 * @param {object} user
 * @param {object} info
 * @param {string} info.baseProgramName  - program they started from
 * @param {string[]} info.deliverables   - deliverable names they ended up with
 * @param {string[]} [info.added]        - names added on top of the program
 * @param {string[]} [info.removed]      - program deliverables they took out
 * @param {number} [info.implementationHours] - hours on the pooled Implementation Hours line
 * @param {number} [info.pricePerMonth]
 * @param {string} [info.billingCycle]
 */
export function logProgramCustomized(user, info = {}) {
  const {
    baseProgramName,
    deliverables = [],
    added = [],
    removed = [],
    implementationHours,
    pricePerMonth,
    billingCycle,
  } = info;
  void logStartActivity(user, {
    key: `program_customized_${baseProgramName || 'program'}`,
    label: `Customized the ${baseProgramName || 'program'}`,
    kind: 'program_customized',
    detail: pruneUndefined({
      baseProgramName,
      deliverables,
      added,
      removed,
      deliverableCount: deliverables.length,
      implementationHours,
      pricePerMonth,
      billingCycle,
    }),
    path: '/start/programdetail',
  });
}

/**
 * The lead committed to a program (clicked through to checkout).
 * @param {object} user
 * @param {object} info
 */
export function logProgramChosen(user, info = {}) {
  const {
    programName,
    tier,
    billingCycle,
    pricePerMonth,
    minimumTerm,
    implementationHours,
    deliverables = [],
    isCustomized,
  } = info;
  if (!programName) return;
  void logStartActivity(user, {
    key: `program_chosen_${programName}_${tier || 'default'}`,
    label: `Chose the ${programName}${tier && tier !== programName ? ` — ${tier}` : ''}`,
    kind: 'program_chosen',
    detail: pruneUndefined({
      programName,
      tier,
      billingCycle,
      pricePerMonth,
      minimumTerm,
      implementationHours,
      deliverables,
      deliverableCount: deliverables.length,
      isCustomized: !!isCustomized,
    }),
    path: '/start/checkout',
  });
}

// ---------------------------------------------------------------------------
// Reads (CRM side)
// ---------------------------------------------------------------------------

/**
 * Load a portal user's Start activity, newest first.
 * Returns [] rather than throwing so the Lead overview still renders.
 *
 * @param {string} userId - the portal user's Firebase UID
 * @returns {Promise<Array<object>>}
 */
export async function fetchStartActivity(userId) {
  if (!userId) return [];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'users', userId, START_ACTIVITY_COLLECTION),
        orderBy('at', 'desc'),
        limit(START_ACTIVITY_READ_LIMIT)
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.warn('[startActivity] failed to load activity for', userId, error);
    return [];
  }
}
